import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  TruckIcon, Package, MapPin, CheckCircle, RotateCcw, AlertTriangle,
  DollarSign, ShieldCheck, Eye, RefreshCw, Banknote, CreditCard,
  Warehouse, Navigation, PackageCheck, Undo2, Clock, FileText,
  Download, FileSignature, UserPlus, Building2, AlertOctagon,
  CircleCheck, Plus, Trash2, Receipt
} from 'lucide-react';

const ESTADOS_DIST = [
  { value: 'almacen_rd', label: 'Almacen RD', icon: <Warehouse className="h-4 w-4" />, color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'en_ruta', label: 'En Ruta', icon: <Navigation className="h-4 w-4" />, color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'entregada', label: 'Entregada', icon: <PackageCheck className="h-4 w-4" />, color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'devuelta', label: 'Devuelta', icon: <Undo2 className="h-4 w-4" />, color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'pendiente_articulo', label: 'Art. Pendiente', icon: <Clock className="h-4 w-4" />, color: 'bg-amber-100 text-amber-800 border-amber-300' },
];

const DistribucionRD = () => {
  const [furgones, setFurgones] = useState([]);
  const [selectedFurgon, setSelectedFurgon] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('todos');

  // Dialogs
  const [autorizarDialog, setAutorizarDialog] = useState({ open: false, factura: null });
  const [cobrarDialog, setCobrarDialog] = useState({ open: false, factura: null });
  const [autorizadasDialog, setAutorizadasDialog] = useState(false);
  const [cobrarForm, setCobrarForm] = useState({ monto: 0, metodo_pago_rd: 'efectivo_rd' });
  const [autorizarNotas, setAutorizarNotas] = useState('');

  // Distribuidor state
  const [distribuidores, setDistribuidores] = useState([]);
  const [distDialog, setDistDialog] = useState(false);
  const [distForm, setDistForm] = useState({ nombre: '', cedula_rnc: '', telefono: '', direccion: '', contacto: '', email: '', password: '' });
  const [contratoEstado, setContratoEstado] = useState(null);
  const [alertaContrato, setAlertaContrato] = useState(false);
  const [contratoDialog, setContratoDialog] = useState(false);
  const [asignarDialog, setAsignarDialog] = useState(false);
  const [selectedDistId, setSelectedDistId] = useState('');

  // Factura distribuidor state
  const [factDistDialog, setFactDistDialog] = useState(false);
  const [factDistData, setFactDistData] = useState(null);
  const [factDistConceptos, setFactDistConceptos] = useState([
    { descripcion: 'Distribucion de paquetes', cantidad: 1, precio_unitario: 0, subtotal: 0 }
  ]);

  useEffect(() => { fetchFurgones(); fetchDistribuidores(); }, []);

  const fetchFurgones = async () => {
    try {
      const res = await api.get('/furgones');
      setFurgones(res.data.data || []);
    } catch (error) { console.error('Error loading furgones'); }
  };

  const fetchDistribuidores = async () => {
    try {
      const res = await api.get('/distribucion/distribuidores');
      setDistribuidores(res.data || []);
    } catch (error) { console.error('Error loading distribuidores'); }
  };

  const fetchDistribucion = useCallback(async () => {
    if (!selectedFurgon) return;
    setLoading(true);
    try {
      const [distRes, contratoRes] = await Promise.all([
        api.get(`/distribucion/furgon/${selectedFurgon}`),
        api.get(`/distribucion/furgon/${selectedFurgon}/contrato-estado`).catch(() => ({ data: null })),
      ]);
      setData(distRes.data);
      setContratoEstado(contratoRes.data);
      // Load factura distribuidor
      try {
        const fdRes = await api.get(`/distribucion/furgon/${selectedFurgon}/factura-distribuidor`);
        setFactDistData(fdRes.data);
      } catch { setFactDistData(null); }
    } catch (error) {
      toast.error('Error al cargar datos del furgon');
    } finally {
      setLoading(false);
    }
  }, [selectedFurgon]);

  useEffect(() => {
    if (selectedFurgon) fetchDistribucion();
  }, [selectedFurgon, fetchDistribucion]);

  // === ACTIONS ===
  const cambiarEstado = async (facturaId, nuevoEstado) => {
    try {
      await api.patch(`/distribucion/factura/${facturaId}/estado`, { estado_distribucion: nuevoEstado });
      toast.success('Estado actualizado');
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleAutorizar = async () => {
    if (!autorizarDialog.factura) return;
    try {
      const res = await api.post(`/distribucion/factura/${autorizarDialog.factura.id}/autorizar`, { notas: autorizarNotas });
      toast.success(res.data.message);
      setAutorizarDialog({ open: false, factura: null });
      setAutorizarNotas('');
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al autorizar'); }
  };

  const handleRevocar = async (facturaId) => {
    if (!window.confirm('Revocar autorizacion de entrega sin pago?')) return;
    try {
      await api.post(`/distribucion/factura/${facturaId}/revocar-autorizacion`);
      toast.success('Autorizacion revocada');
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handleCobrarRD = async () => {
    if (!cobrarDialog.factura || cobrarForm.monto <= 0) return;
    try {
      const res = await api.patch(`/distribucion/factura/${cobrarDialog.factura.id}/cobrar-rd`, cobrarForm);
      toast.success(res.data.message);
      setCobrarDialog({ open: false, factura: null });
      setCobrarForm({ monto: 0, metodo_pago_rd: 'efectivo_rd' });
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const openCobrarDialog = (factura) => {
    setCobrarForm({ monto: factura.monto_pendiente, metodo_pago_rd: 'efectivo_rd' });
    setCobrarDialog({ open: true, factura });
  };

  // === DISTRIBUIDOR ACTIONS ===
  const handleCrearDistribuidor = async () => {
    if (!distForm.nombre.trim()) return toast.error('El nombre es requerido');
    try {
      await api.post('/distribucion/distribuidores', distForm);
      toast.success('Distribuidor creado');
      setDistDialog(false);
      setDistForm({ nombre: '', cedula_rnc: '', telefono: '', direccion: '', contacto: '', email: '', password: '' });
      fetchDistribuidores();
    } catch (error) { toast.error('Error al crear distribuidor'); }
  };

  const handleAsignarDistribuidor = async () => {
    if (!selectedDistId) return toast.error('Seleccione un distribuidor');
    try {
      const res = await api.post(`/distribucion/furgon/${selectedFurgon}/asignar-distribuidor`, { distribuidor_id: selectedDistId });
      toast.success(res.data.message);
      setAsignarDialog(false);
      setSelectedDistId('');
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  // === CONTRACT ACTIONS ===
  const handleDescargarContrato = async () => {
    try {
      const res = await api.get(`/distribucion/furgon/${selectedFurgon}/contrato-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrato_distribucion_${selectedFurgon}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Contrato descargado');
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error descargando contrato'); }
  };

  const handleFirmarContrato = async () => {
    try {
      const res = await api.post(`/distribucion/furgon/${selectedFurgon}/firmar-contrato`);
      toast.success(res.data.message);
      setContratoDialog(false);
      fetchDistribucion();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al firmar'); }
  };

  const handleDescargarFacturas = () => {
    if (!contratoEstado?.contrato_firmado) {
      setAlertaContrato(true);
      return;
    }
    // Download all invoices PDF
    descargarFacturasPDF();
  };

  const descargarFacturasPDF = async () => {
    try {
      const res = await api.get(`/reports/furgon/${selectedFurgon}/facturas-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_furgon_${selectedFurgon}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Facturas descargadas');
    } catch (error) { toast.error('Error descargando facturas'); }
  };

  // === FACTURA DISTRIBUIDOR ACTIONS ===
  const handleGuardarFactDist = async () => {
    const conceptosValidos = factDistConceptos.filter(c => c.descripcion.trim());
    if (!conceptosValidos.length) return toast.error('Agregue al menos un concepto');
    try {
      await api.post(`/distribucion/furgon/${selectedFurgon}/factura-distribuidor`, {
        conceptos: conceptosValidos,
        notas: ''
      });
      toast.success('Factura de distribuidor guardada');
      setFactDistDialog(false);
      fetchDistribucion();
    } catch (error) { toast.error('Error guardando factura'); }
  };

  const handleDescargarFactDist = async () => {
    try {
      const res = await api.get(`/distribucion/furgon/${selectedFurgon}/factura-distribuidor-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_distribuidor_${selectedFurgon}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const handlePagarFactDist = async () => {
    if (!window.confirm('Marcar la factura del distribuidor como PAGADA?')) return;
    try {
      await api.patch(`/distribucion/furgon/${selectedFurgon}/factura-distribuidor/pagar`);
      toast.success('Factura marcada como pagada');
      fetchDistribucion();
    } catch (error) { toast.error('Error'); }
  };

  const updateConcepto = (idx, field, val) => {
    setFactDistConceptos(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: val };
      if (field === 'cantidad' || field === 'precio_unitario') {
        copy[idx].subtotal = (copy[idx].cantidad || 0) * (copy[idx].precio_unitario || 0);
      }
      return copy;
    });
  };

  // Filter facturas
  const facturasFiltradas = data?.facturas?.filter(f => {
    if (filtro === 'todos') return true;
    if (filtro === 'autorizadas') return f.autorizado_sin_pago;
    if (filtro === 'pendiente_cobro') return f.monto_pendiente > 0 && !f.autorizado_sin_pago;
    return f.estado_distribucion === filtro;
  }) || [];

  const autorizadas = data?.facturas?.filter(f => f.autorizado_sin_pago) || [];
  const s = data?.stats || {};
  const t = data?.totales || {};
  const getEstadoInfo = (estado) => ESTADOS_DIST.find(e => e.value === estado) || ESTADOS_DIST[0];
  const contratoFirmado = contratoEstado?.contrato_firmado;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-3 sm:p-6 pb-24" data-testid="distribucion-page">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* HEADER */}
        <Card className="shadow-xl border-2 border-indigo-100">
          <CardHeader className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white rounded-t-lg py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <TruckIcon className="h-7 w-7" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">Distribucion RD</CardTitle>
                  <p className="text-indigo-200 text-sm">Cuadre por Furgon - Control de Facturas</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={selectedFurgon} onValueChange={setSelectedFurgon}>
                  <SelectTrigger data-testid="select-furgon" className="h-10 w-48 bg-white/20 border-white/30 text-white">
                    <SelectValue placeholder="Seleccionar Furgon..." />
                  </SelectTrigger>
                  <SelectContent>
                    {furgones.map(f => (
                      <SelectItem key={f.id} value={f.numero}>
                        {f.numero} ({f.shipment_count || 0} envios)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedFurgon && (
                  <Button data-testid="refresh-dist" variant="secondary" size="sm" onClick={fetchDistribucion} className="h-10 bg-white/20 text-white hover:bg-white/30 border-white/30">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {!selectedFurgon ? (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="py-16 text-center">
              <TruckIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-lg text-gray-500 font-medium">Selecciona un furgon para ver las facturas</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
          </div>
        ) : data && (
          <>
            {/* DISTRIBUIDOR + CONTRATO BAR */}
            <Card className="border-2 border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Distribuidor info */}
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Building2 className="h-5 w-5 text-amber-700" />
                    {contratoEstado?.tiene_distribuidor ? (
                      <span className="font-bold text-amber-900">{contratoEstado.distribuidor_nombre}</span>
                    ) : (
                      <span className="text-amber-600 text-sm">Sin distribuidor asignado</span>
                    )}
                    <Button data-testid="btn-asignar-dist" variant="outline" size="sm" onClick={() => setAsignarDialog(true)}
                      className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100 ml-1">
                      {contratoEstado?.tiene_distribuidor ? 'Cambiar' : 'Asignar'}
                    </Button>
                    <Button data-testid="btn-nuevo-dist" variant="ghost" size="sm" onClick={() => setDistDialog(true)}
                      className="h-7 text-xs text-amber-600">
                      <UserPlus className="h-3 w-3 mr-1" /> Nuevo
                    </Button>
                  </div>

                  {/* Contract status */}
                  {contratoEstado?.tiene_distribuidor && (
                    <div className="flex items-center gap-2">
                      {contratoFirmado ? (
                        <Badge data-testid="badge-contrato-firmado" className="bg-green-100 text-green-800 border-green-400 gap-1">
                          <CircleCheck className="h-3.5 w-3.5" /> Contrato Firmado
                        </Badge>
                      ) : (
                        <Badge data-testid="badge-contrato-pendiente" className="bg-red-100 text-red-800 border-red-400 gap-1 animate-pulse">
                          <AlertOctagon className="h-3.5 w-3.5" /> Contrato Pendiente
                        </Badge>
                      )}
                      <Button data-testid="btn-contrato" variant="outline" size="sm" onClick={() => setContratoDialog(true)}
                        className="h-7 text-xs border-indigo-300 text-indigo-700">
                        <FileSignature className="h-3 w-3 mr-1" /> Contrato
                      </Button>
                    </div>
                  )}

                  {/* Download All Invoices - MAIN BUTTON */}
                  {data?.facturas?.length > 0 && (
                    <Button data-testid="btn-descargar-facturas" onClick={handleDescargarFacturas}
                      className={`h-9 gap-2 font-bold ${contratoFirmado ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                      <Download className="h-4 w-4" /> Descargar Todas las Facturas
                    </Button>
                  )}

                  {/* Factura Distribuidor */}
                  {contratoEstado?.tiene_distribuidor && (
                    <Button data-testid="btn-fact-dist" variant="outline" size="sm" onClick={() => {
                      if (factDistData?.conceptos) {
                        setFactDistConceptos(factDistData.conceptos);
                      }
                      setFactDistDialog(true);
                    }} className="h-9 text-xs border-purple-300 text-purple-700 hover:bg-purple-50 gap-1">
                      <Receipt className="h-3.5 w-3.5" /> Pago Distribuidor
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* DISTRIBUTION STATUS CARDS */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard label="Total" value={s.total_facturas} color="blue" icon={<FileText className="h-5 w-5" />} testId="stat-total" />
              <StatCard label="Almacen RD" value={s.en_almacen} color="purple" icon={<Warehouse className="h-5 w-5" />} testId="stat-almacen" />
              <StatCard label="En Ruta" value={s.en_ruta} color="orange" icon={<Navigation className="h-5 w-5" />} testId="stat-ruta" />
              <StatCard label="Entregadas" value={s.entregadas} color="green" icon={<PackageCheck className="h-5 w-5" />} testId="stat-entregadas" />
              <StatCard label="Devueltas" value={s.devueltas} color="red" icon={<Undo2 className="h-5 w-5" />} testId="stat-devueltas" />
              <StatCard label="Art. Pend." value={s.pendiente_articulo} color="amber" icon={<Clock className="h-5 w-5" />} testId="stat-articulo" />
            </div>

            {/* PAYMENT SUMMARY */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="border-2 border-green-100">
                <CardHeader className="py-3 bg-green-50 border-b border-green-100">
                  <CardTitle className="text-base flex items-center gap-2 text-green-800">
                    <DollarSign className="h-5 w-5" /> Desglose de Cobros
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  <PaymentRow label="Cobrado USA" value={t.cobrado_usa} color="green" icon={<Banknote className="h-4 w-4" />} />
                  <PaymentRow label="Cobrado RD" value={t.cobrado_rd} color="blue" icon={<MapPin className="h-4 w-4" />} />
                  <PaymentRow label="Pendiente Cobro" value={t.pendiente_cobro} color="red" icon={<Clock className="h-4 w-4" />} />
                  <div className="border-t-2 pt-2 flex justify-between items-center">
                    <span className="font-bold text-gray-700">Total Furgon</span>
                    <span className="text-xl font-black text-blue-700">${(t.total_furgon || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-100">
                <CardHeader className="py-3 bg-emerald-50 border-b border-emerald-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                      <ShieldCheck className="h-5 w-5" /> Autorizadas Sin Pago
                    </CardTitle>
                    {autorizadas.length > 0 && (
                      <Button data-testid="btn-ver-autorizadas" variant="outline" size="sm" onClick={() => setAutorizadasDialog(true)}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <Eye className="h-4 w-4 mr-1" /> Ver Todas ({autorizadas.length})
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <span className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                      <ShieldCheck className="h-5 w-5" /> Facturas Autorizadas
                    </span>
                    <div className="text-right">
                      <p className="text-2xl font-black text-emerald-600">{s.autorizadas_sin_pago || 0}</p>
                      <p className="text-xs text-emerald-500 font-semibold">${(t.total_autorizado_sin_pago || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                      <TruckIcon className="h-5 w-5" /> Distribuidor Debe Entregar
                    </span>
                    <p className="text-2xl font-black text-blue-600">${(t.cobrado_rd || 0).toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* FILTER TABS */}
            <div className="flex flex-wrap gap-2">
              {[
                { val: 'todos', label: 'Todas', count: s.total_facturas },
                { val: 'almacen_rd', label: 'Almacen', count: s.en_almacen },
                { val: 'en_ruta', label: 'En Ruta', count: s.en_ruta },
                { val: 'entregada', label: 'Entregadas', count: s.entregadas },
                { val: 'devuelta', label: 'Devueltas', count: s.devueltas },
                { val: 'pendiente_articulo', label: 'Art. Pend.', count: s.pendiente_articulo },
                { val: 'autorizadas', label: 'Autorizadas', count: s.autorizadas_sin_pago },
                { val: 'pendiente_cobro', label: 'Pend. Cobro', count: null },
              ].map(tab => (
                <Button key={tab.val} data-testid={`filter-${tab.val}`}
                  variant={filtro === tab.val ? 'default' : 'outline'} size="sm"
                  onClick={() => setFiltro(tab.val)}
                  className={`text-xs font-bold ${filtro === tab.val ? 'bg-indigo-600' : ''}`}>
                  {tab.label} {tab.count != null ? `(${tab.count})` : ''}
                </Button>
              ))}
            </div>

            {/* FACTURAS TABLE */}
            <Card className="border-2 border-indigo-100">
              <CardHeader className="py-3 bg-indigo-50 border-b border-indigo-100">
                <CardTitle className="text-base flex items-center gap-2 text-indigo-800">
                  <FileText className="h-5 w-5" /> Facturas - Furgon {selectedFurgon} ({facturasFiltradas.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {facturasFiltradas.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No hay facturas con este filtro</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-indigo-50/50">
                          <TableHead className="font-bold text-xs">Factura</TableHead>
                          <TableHead className="font-bold text-xs">Destinatario</TableHead>
                          <TableHead className="font-bold text-xs text-right">Total</TableHead>
                          <TableHead className="font-bold text-xs text-right">Pagado</TableHead>
                          <TableHead className="font-bold text-xs text-right">Pendiente</TableHead>
                          <TableHead className="font-bold text-xs text-center">Estado Dist.</TableHead>
                          <TableHead className="font-bold text-xs text-center">Autorizada</TableHead>
                          <TableHead className="font-bold text-xs text-center">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {facturasFiltradas.map((f) => {
                          const estadoInfo = getEstadoInfo(f.estado_distribucion);
                          return (
                            <TableRow key={f.id} data-testid={`dist-row-${f.numero_factura}`} className={f.autorizado_sin_pago ? 'bg-emerald-50/40' : ''}>
                              <TableCell className="font-bold text-indigo-600">{f.numero_factura}</TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="font-semibold truncate max-w-[140px]">{f.destinatario_nombre}</p>
                                  <p className="text-xs text-gray-400 truncate max-w-[140px]">{f.destinatario_ciudad}</p>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-bold">${f.total?.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-bold text-green-600">${f.monto_pagado?.toFixed(2)}</TableCell>
                              <TableCell className={`text-right font-bold ${f.monto_pendiente > 0 ? 'text-red-600' : 'text-gray-300'}`}>
                                ${f.monto_pendiente?.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-center">
                                <Select value={f.estado_distribucion || 'almacen_rd'} onValueChange={(v) => cambiarEstado(f.id, v)}>
                                  <SelectTrigger className={`h-8 text-xs font-bold border ${estadoInfo.color} w-36`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ESTADOS_DIST.map(e => (
                                      <SelectItem key={e.value} value={e.value}>
                                        <span className="flex items-center gap-1">{e.icon} {e.label}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-center">
                                {f.autorizado_sin_pago ? (
                                  <button onClick={() => handleRevocar(f.id)}
                                    data-testid={`autorizada-badge-${f.id}`}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border-2 border-emerald-400 hover:bg-emerald-200 transition-colors cursor-pointer animate-pulse"
                                    title={`Autorizada por ${f.autorizado_por} - Click para revocar`}>
                                    <ShieldCheck className="h-3.5 w-3.5" /> {f.autorizado_por?.split(' ')[0]}
                                  </button>
                                ) : f.monto_pendiente > 0 ? (
                                  <Button data-testid={`btn-autorizar-${f.id}`} variant="ghost" size="sm"
                                    onClick={() => { setAutorizarDialog({ open: true, factura: f }); setAutorizarNotas(''); }}
                                    className="h-7 text-xs text-gray-400 hover:text-emerald-600">
                                    Autorizar
                                  </Button>
                                ) : (
                                  <span className="text-gray-300 text-xs">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {f.monto_pendiente > 0 && !f.autorizado_sin_pago && (
                                  <Button data-testid={`btn-cobrar-rd-${f.id}`} variant="outline" size="sm"
                                    onClick={() => openCobrarDialog(f)}
                                    className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50">
                                    <MapPin className="h-3 w-3 mr-1" /> Cobrar RD
                                  </Button>
                                )}
                                {f.cobrado_en_rd > 0 && (
                                  <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs ml-1">
                                    RD: ${f.cobrado_en_rd.toFixed(2)}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* =================== DIALOGS =================== */}

        {/* ALERTA ROJA - CONTRATO NO FIRMADO */}
        <Dialog open={alertaContrato} onOpenChange={setAlertaContrato}>
          <DialogContent className="max-w-md border-4 border-red-500" data-testid="alerta-contrato-dialog" aria-describedby="alerta-contrato-desc">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center animate-pulse">
                <AlertOctagon className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-xl font-black text-red-700">CONTRATO NO FIRMADO</h2>
              <p id="alerta-contrato-desc" className="text-sm text-red-600 leading-relaxed">
                Para descargar las facturas del furgon <strong>{selectedFurgon}</strong>, el distribuidor
                debe <strong>LEER y FIRMAR</strong> el contrato de distribucion primero.
              </p>
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-left space-y-2">
                <p className="text-xs font-bold text-red-800 uppercase">El contrato incluye:</p>
                <ul className="text-xs text-red-700 space-y-1">
                  <li>- Articulos escaneados en USA y faltantes</li>
                  <li>- Responsabilidad total por la mercancia</li>
                  <li>- Clausula de confidencialidad de datos</li>
                  <li>- Instrucciones de trato al cliente</li>
                  <li>- Penalizaciones por incumplimiento</li>
                </ul>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setAlertaContrato(false); setContratoDialog(true); }}
                  className="flex-1 bg-red-600 hover:bg-red-700 h-11 font-bold gap-2" data-testid="btn-ir-contrato">
                  <FileSignature className="h-4 w-4" /> Ir al Contrato
                </Button>
                <Button variant="outline" onClick={() => setAlertaContrato(false)} className="border-red-300 text-red-600">
                  Cerrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* CONTRATO DIALOG */}
        <Dialog open={contratoDialog} onOpenChange={setContratoDialog}>
          <DialogContent className="max-w-lg" data-testid="contrato-dialog" aria-describedby="contrato-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-amber-700" /> Contrato de Distribucion - {selectedFurgon}
              </DialogTitle>
              <p id="contrato-desc" className="text-sm text-gray-500">Gestionar el contrato con el distribuidor</p>
            </DialogHeader>
            <div className="space-y-4">
              {contratoEstado?.tiene_distribuidor ? (
                <>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="font-bold text-amber-900 flex items-center gap-2">
                      <Building2 className="h-4 w-4" /> {contratoEstado.distribuidor_nombre}
                    </p>
                  </div>

                  {contratoFirmado ? (
                    <div className="p-4 bg-green-50 border-2 border-green-300 rounded-xl text-center">
                      <CircleCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-bold text-green-800">Contrato Firmado</p>
                      <p className="text-xs text-green-600">Firmado por: {contratoEstado.firmado_por}</p>
                      <p className="text-xs text-green-600">Fecha: {contratoEstado.fecha_firma?.slice(0, 10)}</p>
                    </div>
                  ) : (
                    <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                      <p className="font-bold text-red-800 flex items-center gap-2 mb-2">
                        <AlertOctagon className="h-4 w-4" /> Contrato Pendiente de Firma
                      </p>
                      <p className="text-xs text-red-600">
                        Descargue y lea el contrato completo. Luego acepte los terminos para poder descargar las facturas.
                      </p>
                    </div>
                  )}

                  <Button onClick={handleDescargarContrato} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="btn-descargar-contrato">
                    <Download className="h-4 w-4" /> Descargar Contrato PDF
                  </Button>

                  {!contratoFirmado && (
                    <Button onClick={handleFirmarContrato} className="w-full h-11 bg-green-600 hover:bg-green-700 gap-2 font-bold" data-testid="btn-firmar-contrato">
                      <FileSignature className="h-4 w-4" /> Acepto los Terminos - Firmar Digitalmente
                    </Button>
                  )}

                  {contratoFirmado && (
                    <Button onClick={() => { setContratoDialog(false); descargarFacturasPDF(); }}
                      className="w-full h-11 bg-green-600 hover:bg-green-700 gap-2 font-bold" data-testid="btn-descargar-facturas-contrato">
                      <Download className="h-4 w-4" /> Descargar Todas las Facturas
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Asigne un distribuidor primero</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ASIGNAR DISTRIBUIDOR DIALOG */}
        <Dialog open={asignarDialog} onOpenChange={setAsignarDialog}>
          <DialogContent className="max-w-md" data-testid="asignar-dist-dialog" aria-describedby="asignar-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-amber-600" /> Asignar Distribuidor
              </DialogTitle>
              <p id="asignar-desc" className="text-sm text-gray-500">Seleccione la compania distribuidora para este furgon</p>
            </DialogHeader>
            <div className="space-y-4">
              {distribuidores.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-gray-400 mb-3">No hay distribuidores registrados</p>
                  <Button onClick={() => { setAsignarDialog(false); setDistDialog(true); }} className="bg-amber-600 hover:bg-amber-700 gap-2">
                    <UserPlus className="h-4 w-4" /> Crear Distribuidor
                  </Button>
                </div>
              ) : (
                <>
                  <Select value={selectedDistId} onValueChange={setSelectedDistId}>
                    <SelectTrigger data-testid="select-distribuidor" className="h-11">
                      <SelectValue placeholder="Seleccionar distribuidor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {distribuidores.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.nombre} {d.cedula_rnc ? `(${d.cedula_rnc})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button onClick={handleAsignarDistribuidor} className="flex-1 h-11 bg-amber-600 hover:bg-amber-700" data-testid="submit-asignar">
                      Asignar Distribuidor
                    </Button>
                    <Button variant="outline" onClick={() => { setAsignarDialog(false); setDistDialog(true); }} className="h-11 gap-1">
                      <UserPlus className="h-4 w-4" /> Nuevo
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* CREAR DISTRIBUIDOR DIALOG */}
        <Dialog open={distDialog} onOpenChange={setDistDialog}>
          <DialogContent className="max-w-md" data-testid="crear-dist-dialog" aria-describedby="crear-dist-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-indigo-600" /> Registrar Compania Distribuidora
              </DialogTitle>
              <p id="crear-dist-desc" className="text-sm text-gray-500">Complete los datos de la compania</p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="font-semibold">Nombre de la Compania *</Label>
                <Input data-testid="dist-nombre" value={distForm.nombre} onChange={e => setDistForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Distribuidora Express RD" className="h-11 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">RNC / Cedula</Label>
                  <Input data-testid="dist-rnc" value={distForm.cedula_rnc} onChange={e => setDistForm(p => ({ ...p, cedula_rnc: e.target.value }))}
                    placeholder="012-3456789-0" className="h-11 mt-1" />
                </div>
                <div>
                  <Label className="font-semibold">Telefono</Label>
                  <Input data-testid="dist-tel" value={distForm.telefono} onChange={e => setDistForm(p => ({ ...p, telefono: e.target.value }))}
                    placeholder="809-555-1234" className="h-11 mt-1" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Direccion</Label>
                <Input value={distForm.direccion} onChange={e => setDistForm(p => ({ ...p, direccion: e.target.value }))}
                  placeholder="Calle, Ciudad, Provincia" className="h-11 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold">Contacto</Label>
                  <Input value={distForm.contacto} onChange={e => setDistForm(p => ({ ...p, contacto: e.target.value }))}
                    placeholder="Nombre del contacto" className="h-11 mt-1" />
                </div>
                <div>
                  <Label className="font-semibold">Email</Label>
                  <Input value={distForm.email} onChange={e => setDistForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="correo@empresa.com" className="h-11 mt-1" />
                </div>
              </div>
              <div>
                <Label className="font-semibold">Contrasena del Portal</Label>
                <Input data-testid="dist-password" type="password" value={distForm.password}
                  onChange={e => setDistForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Contrasena para acceso al portal" className="h-11 mt-1" />
                <p className="text-xs text-gray-400 mt-1">Si asigna email y contrasena, el distribuidor podra acceder a su portal</p>
              </div>
              <Button onClick={handleCrearDistribuidor} className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 font-bold" data-testid="submit-crear-dist">
                <UserPlus className="h-4 w-4 mr-2" /> Registrar Compania
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* FACTURA DISTRIBUIDOR DIALOG */}
        <Dialog open={factDistDialog} onOpenChange={setFactDistDialog}>
          <DialogContent className="max-w-lg" data-testid="fact-dist-dialog" aria-describedby="fact-dist-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-purple-600" /> Factura de Pago al Distribuidor
              </DialogTitle>
              <p id="fact-dist-desc" className="text-sm text-gray-500">Furgon {selectedFurgon} - {contratoEstado?.distribuidor_nombre}</p>
            </DialogHeader>
            <div className="space-y-4">
              {factDistData && (
                <div className={`p-3 rounded-lg border text-sm font-semibold ${factDistData.estado === 'pagado' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                  {factDistData.numero_factura} - {factDistData.estado?.toUpperCase()} - Total: ${(factDistData.total || 0).toFixed(2)}
                </div>
              )}

              <div className="space-y-2">
                <Label className="font-semibold">Conceptos de Pago</Label>
                {factDistConceptos.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Input placeholder="Descripcion" value={c.descripcion}
                        onChange={e => updateConcepto(i, 'descripcion', e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" placeholder="Cant" value={c.cantidad || ''}
                        onChange={e => updateConcepto(i, 'cantidad', parseInt(e.target.value) || 0)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" step="0.01" placeholder="Precio" value={c.precio_unitario || ''}
                        onChange={e => updateConcepto(i, 'precio_unitario', parseFloat(e.target.value) || 0)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-2 text-right text-sm font-bold text-gray-700 py-2">
                      ${(c.subtotal || 0).toFixed(2)}
                    </div>
                    <div className="col-span-1">
                      {factDistConceptos.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => setFactDistConceptos(p => p.filter((_, idx) => idx !== i))}
                          className="h-9 w-9 p-0 text-red-400 hover:text-red-600">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFactDistConceptos(p => [...p, { descripcion: '', cantidad: 1, precio_unitario: 0, subtotal: 0 }])}
                  className="text-xs gap-1">
                  <Plus className="h-3 w-3" /> Agregar Concepto
                </Button>
              </div>

              <div className="border-t-2 pt-3 flex justify-between items-center">
                <span className="font-bold text-gray-700">TOTAL A PAGAR:</span>
                <span className="text-xl font-black text-purple-700">
                  ${factDistConceptos.reduce((s, c) => s + (c.subtotal || 0), 0).toFixed(2)}
                </span>
              </div>

              <Button onClick={handleGuardarFactDist} className="w-full h-11 bg-purple-600 hover:bg-purple-700 font-bold" data-testid="submit-fact-dist">
                Guardar Factura
              </Button>

              {factDistData && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleDescargarFactDist} className="flex-1 h-10 gap-2 border-purple-300 text-purple-700" data-testid="btn-descargar-fact-dist">
                    <Download className="h-4 w-4" /> Descargar PDF
                  </Button>
                  {factDistData.estado !== 'pagado' && (
                    <Button variant="outline" onClick={handlePagarFactDist} className="flex-1 h-10 gap-2 border-green-300 text-green-700" data-testid="btn-pagar-fact-dist">
                      <CheckCircle className="h-4 w-4" /> Marcar Pagada
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* AUTORIZAR SIN PAGO DIALOG */}
        <Dialog open={autorizarDialog.open} onOpenChange={(open) => setAutorizarDialog({ open, factura: autorizarDialog.factura })}>
          <DialogContent className="max-w-md" data-testid="autorizar-dialog" aria-describedby="autorizar-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" /> Autorizar Entrega Sin Pago
              </DialogTitle>
              <p id="autorizar-desc" className="text-sm text-gray-500">La factura se entregara sin cobro. Se registrara quien autorizo.</p>
            </DialogHeader>
            {autorizarDialog.factura && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-bold text-amber-800">Factura: {autorizarDialog.factura.numero_factura}</p>
                  <p className="text-sm text-amber-700">Destinatario: {autorizarDialog.factura.destinatario_nombre}</p>
                  <p className="text-sm text-amber-700">Pendiente: <span className="font-bold">${autorizarDialog.factura.monto_pendiente?.toFixed(2)}</span></p>
                </div>
                <div>
                  <Label className="font-semibold">Notas (opcional)</Label>
                  <Input data-testid="autorizar-notas" value={autorizarNotas} onChange={(e) => setAutorizarNotas(e.target.value)}
                    placeholder="Razon de la autorizacion..." className="h-11 mt-1" />
                </div>
                <Button data-testid="submit-autorizar" onClick={handleAutorizar} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Autorizar Entrega Sin Pago
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* COBRAR EN RD DIALOG */}
        <Dialog open={cobrarDialog.open} onOpenChange={(open) => setCobrarDialog({ open, factura: cobrarDialog.factura })}>
          <DialogContent className="max-w-md" data-testid="cobrar-rd-dialog" aria-describedby="cobrar-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" /> Registrar Cobro en RD
              </DialogTitle>
              <p id="cobrar-desc" className="text-sm text-gray-500">Cobro realizado por el distribuidor en Republica Dominicana</p>
            </DialogHeader>
            {cobrarDialog.factura && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-bold text-blue-800">Factura: {cobrarDialog.factura.numero_factura}</p>
                  <p className="text-sm text-blue-700">Destinatario: {cobrarDialog.factura.destinatario_nombre}</p>
                  <p className="text-sm text-blue-700">Total: ${cobrarDialog.factura.total?.toFixed(2)} | Pendiente: <span className="font-bold">${cobrarDialog.factura.monto_pendiente?.toFixed(2)}</span></p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-semibold">Monto Cobrado *</Label>
                    <Input data-testid="cobrar-monto" type="number" step="0.01" min="0"
                      value={cobrarForm.monto || ''} onChange={(e) => setCobrarForm(p => ({ ...p, monto: parseFloat(e.target.value) || 0 }))}
                      className="h-11 mt-1" />
                  </div>
                  <div>
                    <Label className="font-semibold">Metodo de Pago</Label>
                    <Select value={cobrarForm.metodo_pago_rd} onValueChange={(v) => setCobrarForm(p => ({ ...p, metodo_pago_rd: v }))}>
                      <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo_rd">Efectivo RD</SelectItem>
                        <SelectItem value="transferencia_rd">Transferencia RD</SelectItem>
                        <SelectItem value="tarjeta_rd">Tarjeta RD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button data-testid="submit-cobrar-rd" onClick={handleCobrarRD} className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                  <DollarSign className="mr-2 h-4 w-4" /> Registrar Cobro RD
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* TODAS LAS AUTORIZADAS DIALOG */}
        <Dialog open={autorizadasDialog} onOpenChange={setAutorizadasDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="autorizadas-modal" aria-describedby="autorizadas-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-600" /> Facturas Autorizadas Sin Pago ({autorizadas.length})
              </DialogTitle>
              <p id="autorizadas-desc" className="text-sm text-gray-500">Facturas autorizadas para entrega sin cobro</p>
            </DialogHeader>
            <div className="space-y-2">
              {autorizadas.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No hay facturas autorizadas</p>
              ) : autorizadas.map(f => (
                <div key={f.id} data-testid={`autorizada-item-${f.id}`} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                  <div>
                    <p className="font-bold text-emerald-800">{f.numero_factura}</p>
                    <p className="text-sm text-gray-600">{f.destinatario_nombre}</p>
                    <p className="text-xs text-emerald-600">
                      <ShieldCheck className="h-3 w-3 inline mr-1" />
                      Autorizada por <strong>{f.autorizado_por}</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">${f.monto_pendiente?.toFixed(2)}</p>
                    <Badge className="bg-emerald-200 text-emerald-800 border-emerald-400 text-xs">AUTORIZADA</Badge>
                  </div>
                </div>
              ))}
              {autorizadas.length > 0 && (
                <div className="border-t-2 pt-3 flex justify-between items-center">
                  <span className="font-bold text-gray-700">Total Autorizado Sin Cobro</span>
                  <span className="text-xl font-black text-emerald-600">${(t.total_autorizado_sin_pago || 0).toFixed(2)}</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

// Helper components
const StatCard = ({ label, value, color, icon, testId }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={`rounded-xl border-2 p-3 ${colors[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1 opacity-70">{icon}<span className="text-xs font-bold uppercase">{label}</span></div>
      <p className="text-2xl font-black">{value || 0}</p>
    </div>
  );
};

const PaymentRow = ({ label, value, color, icon }) => {
  const textColors = { green: 'text-green-600', blue: 'text-blue-600', red: 'text-red-600' };
  const bgColors = { green: 'bg-green-50', blue: 'bg-blue-50', red: 'bg-red-50' };
  return (
    <div className={`flex justify-between items-center p-2.5 rounded-lg ${bgColors[color]}`}>
      <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">{icon} {label}</span>
      <span className={`text-lg font-bold ${textColors[color]}`}>${(value || 0).toFixed(2)}</span>
    </div>
  );
};

export default DistribucionRD;
